/**
 * Module context — предоставляется SPA shell module bundle компонентам.
 *
 * Module bundles не могут использовать pinia stores из SPA shell (разные pinia instances).
 * Вместо этого SPA shell предоставляет функции через provide/inject.
 *
 * Module компоненты используют useModuleContext() для доступа к toast и auth.
 */
import { inject, type InjectionKey } from 'vue';

export interface ModuleToast {
    push(kind: 'success' | 'error' | 'info' | 'warn', title: string, message?: string): void;
    success(title: string, message?: string): void;
    error(title: string, message?: string): void;
    info(title: string, message?: string): void;
    warn(title: string, message?: string): void;
}

export interface ModuleAuth {
    readonly user: { subject: string; email: string; displayName: string; roles: string[]; role: string } | null;
    readonly isAuthenticated: boolean;
    readonly canAdmin: boolean;
    readonly canMutate: boolean;
}

export interface ModuleTranslate {
    /** Translate a key using the shell's global i18n instance (reactive to locale changes). */
    t(key: string, named?: Record<string, unknown>): string;
    /** Current locale ('ru' | 'en'). */
    readonly locale: string;
}

export interface ModuleContext {
    toast: ModuleToast;
    auth: ModuleAuth;
    i18n: ModuleTranslate;
}

export const ModuleContextKey: InjectionKey<ModuleContext> = 'cavicode.module-context' as unknown as InjectionKey<ModuleContext>;

/** Использовать module context (toast, auth) в module компонентах. */
export function useModuleContext(): ModuleContext | null {
    return inject(ModuleContextKey, null);
}

/** Использовать toast в module компонентах. */
export function useModuleToast(): ModuleToast | null {
    return inject(ModuleContextKey, null)?.toast ?? null;
}

/** Использовать auth в module компонентах. */
export function useModuleAuth(): ModuleAuth | null {
    return inject(ModuleContextKey, null)?.auth ?? null;
}

/** Использовать i18n (t function) в module компонентах. */
export function useModuleI18n(): ModuleTranslate | null {
    return inject(ModuleContextKey, null)?.i18n ?? null;
}